import { useAccountStore } from "../../../entities/account";
import { PROJECTS } from "../data/people";

export type MontageAccount = {
  id: string;
  name: string;
  avatarUrl?: string;
  platform: "instagram" | "tiktok" | "youtube";
};

/** Куда именно уйдёт автопубликация: @паблик или число пабликов в пачке. */
export function instagramPublicationTarget(accounts: MontageAccount[]): string {
  const instagram = [...new Map(accounts.filter((account) => account.platform === "instagram").map((account) => [account.id, account])).values()];
  if (instagram.length === 0) return "Instagram";
  if (instagram.length === 1) return instagram[0].name;

  const n = instagram.length;
  const mod100 = n % 100;
  const mod10 = n % 10;
  const noun = mod100 >= 11 && mod100 <= 14 ? "пабликов" : mod10 === 1 ? "паблик" : mod10 >= 2 && mod10 <= 4 ? "паблика" : "пабликов";
  return `${n} ${noun}`;
}

function platformOf(id: string): MontageAccount["platform"] {
  if (id.startsWith("tt:")) return "tiktok";
  if (id.startsWith("yt:")) return "youtube";
  return "instagram";
}

function displayName(name: string, platform: MontageAccount["platform"]): string {
  if (platform !== "instagram" || name.startsWith("@")) return name;
  return `@${name}`;
}

/**
 * Аккаунты направления для выбора «куда выходит ролик» (с 08.10.2026: одна
 * публикация — один аккаунт). Имена — из общего списка аккаунтов сайта; чего
 * в нём нет (TikTok, чужие), показываем по id.
 */
export function useProjectAccounts(projectId: string | null | undefined): MontageAccount[] {
  const accounts = useAccountStore((s) => s.accounts);
  const project = PROJECTS.find((p) => p.id === projectId);
  return (project?.accountIds ?? []).map((id) => {
    const a = accounts.find((x) => x.id === id);
    const known = a?.username || a?.name || project?.accountNames?.[id];
    const platform = platformOf(id);
    const fallback = platform === "tiktok" ? "TikTok" : platform === "youtube" ? "YouTube" : id;
    return {
      id,
      name: displayName(known || fallback, platform),
      avatarUrl: a?.profilePicture || undefined,
      platform,
    };
  });
}

/** Полные данные паблика для подписи и аватара в карточках и модалках. */
export function useAccountInfo(accountId: string | null | undefined): MontageAccount | null {
  const accounts = useAccountStore((s) => s.accounts);
  if (!accountId) return null;
  const a = accounts.find((x) => x.id === accountId);
  const fromProject = PROJECTS.find((p) => p.accountNames?.[accountId])?.accountNames?.[accountId];
  const platform = platformOf(accountId);
  const fallback = platform === "tiktok" ? "TikTok" : platform === "youtube" ? "YouTube" : accountId;
  return {
    id: accountId,
    name: displayName(a?.username || a?.name || fromProject || fallback, platform),
    avatarUrl: a?.profilePicture || undefined,
    platform,
  };
}

/** Имя аккаунта по id — для карточки. */
export function useAccountName(accountId: string | null | undefined): string | null {
  return useAccountInfo(accountId)?.name ?? null;
}
